'use client'

import { useAudio } from '@/context/AudioContext'
import { useChapter } from '@/context/ChapterContext'
import { useUserPrefs } from '@/context/UserPrefsContext'
import { useVideo } from '@/context/VideoContext'
import { useEffect, useRef, useState } from 'react'
import styles from './subtitles.module.css'

// Interface for a WebVTT cue
interface VTTCue {
  id: string
  startTime: number
  endTime: number
  text: string
}

// Interface for active subtitle cues
interface ActiveCue {
  id: string
  text: string
  language: 'english' | 'maori'
}

// Convert WebVTT time format (HH:MM:SS.mmm) to seconds
function timeStringToSeconds(timeStr: string): number {
  const [hourMinSec, ms] = timeStr.split('.')
  const [hours, minutes, seconds] = hourMinSec.split(':').map(Number)
  return hours * 3600 + minutes * 60 + seconds + Number(ms || 0) / 1000
}

// Parse WebVTT data from text
function parseVTT(vttText: string): VTTCue[] {
  const lines = vttText.split('\n')
  const cues: VTTCue[] = []

  let i = 0
  // Skip WebVTT header (first line with "WEBVTT")
  if (lines.length > 0 && lines[0].includes('WEBVTT')) {
    i = 1
    // Skip any header metadata until we find an empty line
    while (i < lines.length && lines[i].trim() !== '') {
      i++
    }
    // Skip the empty line after header
    if (i < lines.length) i++
  }

  while (i < lines.length) {
    // Skip empty lines
    if (lines[i].trim() === '') {
      i++
      continue
    }

    let id = ''
    let timeLine = lines[i]

    // Check if the current line is a cue ID (doesn't contain "-->")
    if (!timeLine.includes('-->')) {
      id = timeLine.trim()
      i++
      // Get the timing line
      if (i >= lines.length) break
      timeLine = lines[i]
    }

    // Process the timing line
    if (!timeLine || !timeLine.includes('-->')) {
      i++
      continue
    }

    const [startTimeStr, endTimeStr] = timeLine.split('-->').map((t) => t.trim())
    const startTime = timeStringToSeconds(startTimeStr)
    const endTime = timeStringToSeconds(endTimeStr)

    i++

    // Collect text lines until we hit an empty line
    let text = ''
    while (i < lines.length && lines[i].trim() !== '') {
      text += (text ? '\n' : '') + lines[i]
      i++
    }

    if (text) {
      cues.push({
        id: id || `cue-${cues.length + 1}`, // Use provided ID or generate one
        startTime,
        endTime,
        text,
      })
    }

    // The loop will automatically skip empty lines before the next cue
  }

  return cues
}

export default function SubtitleDisplay() {
  const { userPrefs } = useUserPrefs()
  // Use getCurrentChapter from useChapter instead of currentPlaybackChapter
  const { getChapter, currentChapterNumber, showChapterMenu } = useChapter()
  const { currentPosition, currentAudio } = useAudio()
  const { isPlaying } = useVideo()
  const [activeEnglishCues, setActiveEnglishCues] = useState<ActiveCue[]>([])
  const [activeMaoriCues, setActiveMaoriCues] = useState<ActiveCue[]>([])
  const [englishCues, setEnglishCues] = useState<VTTCue[]>([])
  const [maoriCues, setMaoriCues] = useState<VTTCue[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const prevActiveEnglishCues = useRef<ActiveCue[]>([])
  const prevActiveMaoriCues = useRef<ActiveCue[]>([])

  // Load subtitle files for current chapter
  useEffect(() => {
    if (!currentChapterNumber) {
      setEnglishCues([])
      setMaoriCues([])
      return
    }

    console.log(`Loading subtitles for chapter: ${currentChapterNumber}`, 'subtitles')

    const loadSubtitles = async () => {
      setLoading(true)
      setError(null)

      try {
        const chapterId = currentChapterNumber
        const chapterData = getChapter(chapterId)

        // Parse both VTT files
        let parsedEnglishCues: VTTCue[] = []
        let parsedMaoriCues: VTTCue[] = []

        if (chapterData) {
          if (chapterData.englishSubtitle) {
            console.log(`Using English subtitle file: ${chapterData.englishSubtitle}`, 'subtitles')
            // Load English subtitles
            const englishResponse = await fetch(chapterData.englishSubtitle)
            if (!englishResponse.ok) {
              throw new Error(`Failed to load English subtitles for ${chapterId}`)
            }
            const englishText = await englishResponse.text()
            parsedEnglishCues = parseVTT(englishText)
          }

          if (chapterData.maoriSubtitle) {
            console.log(`Using Māori subtitle file: ${chapterData.maoriSubtitle}`, 'subtitles')
            // Load Maori subtitles
            const maoriResponse = await fetch(chapterData.maoriSubtitle)
            if (!maoriResponse.ok) {
              throw new Error(`Failed to load Maori subtitles for ${chapterId}`)
            }
            const maoriText = await maoriResponse.text()
            parsedMaoriCues = parseVTT(maoriText)
          }
        }

        console.log(
          `Loaded ${parsedEnglishCues.length} English cues and ${parsedMaoriCues.length} Māori cues for ${chapterId}`,
          'subtitles'
        )

        // Debug info about cue timings
        if (parsedEnglishCues.length > 0) {
          console.log(`First English cue starts at: ${parsedEnglishCues[0].startTime} seconds`, 'subtitles')
          console.log(`First English cue ends at: ${parsedEnglishCues[0].endTime} seconds`, 'subtitles')
        }
        if (parsedMaoriCues.length > 0) {
          console.log(`First Māori cue starts at: ${parsedMaoriCues[0].startTime} seconds`, 'subtitles')
          console.log(`First Māori cue ends at: ${parsedMaoriCues[0].endTime} seconds`, 'subtitles')
        }

        setEnglishCues(parsedEnglishCues)
        setMaoriCues(parsedMaoriCues)
        setLoading(false)
      } catch (err) {
        console.error(`Error loading subtitles: ${err.message}`, 'subtitles')
        setError(`Failed to load subtitles: ${err instanceof Error ? err.message : String(err)}`)
        setLoading(false)
      }
    }

    loadSubtitles()
  }, [currentChapterNumber, getChapter])

  // Track active cues based on current playback position
  useEffect(() => {
    // Skip if not playing or no audio (removed for testing)
    if (!isPlaying || !currentAudio) {
      return
    }

    // Find active English cues
    const currentEnglishCues = englishCues
      .filter((cue) => currentPosition >= cue.startTime && currentPosition < cue.endTime)
      .map((cue) => ({ id: cue.id, text: cue.text, language: 'english' as const }))

    // Find active Maori cues
    const currentMaoriCues = maoriCues
      .filter((cue) => currentPosition >= cue.startTime && currentPosition < cue.endTime)
      .map((cue) => ({ id: cue.id, text: cue.text, language: 'maori' as const }))

    setActiveEnglishCues(currentEnglishCues)
    setActiveMaoriCues(currentMaoriCues)

    // Determine which languages to log based on user preferences
    const { subtitleLanguage } = userPrefs
    const showEnglish = subtitleLanguage === 'english' || subtitleLanguage === 'both'
    const showMaori = subtitleLanguage === 'maori' || subtitleLanguage === 'both'

    // Log cue changes only for active languages
    if (showEnglish) {
      const prevEnglish = prevActiveEnglishCues.current
      const showingEnglish = currentEnglishCues.filter((cue) => !prevEnglish.some((p) => p.id === cue.id))
      const hidingEnglish = prevEnglish.filter((cue) => !currentEnglishCues.some((c) => c.id === cue.id))

      for (const cue of showingEnglish) {
        console.log(`Reached time ${currentPosition.toFixed(2)}, showing English text: "${cue.text}"`, 'subtitles')
      }
      for (const cue of hidingEnglish) {
        console.log(`Reached time ${currentPosition.toFixed(2)}, hiding English text: "${cue.text}"`, 'subtitles')
      }
    }

    if (showMaori) {
      const prevMaori = prevActiveMaoriCues.current
      const showingMaori = currentMaoriCues.filter((cue) => !prevMaori.some((p) => p.id === cue.id))
      const hidingMaori = prevMaori.filter((cue) => !currentMaoriCues.some((c) => c.id === cue.id))

      for (const cue of showingMaori) {
        console.log(`Reached time ${currentPosition.toFixed(2)}, showing Māori text: "${cue.text}"`, 'subtitles')
      }
      for (const cue of hidingMaori) {
        console.log(`Reached time ${currentPosition.toFixed(2)}, hiding Māori text: "${cue.text}"`, 'subtitles')
      }
    }

    // Update previous refs
    prevActiveEnglishCues.current = currentEnglishCues
    prevActiveMaoriCues.current = currentMaoriCues
  }, [currentPosition, currentAudio, isPlaying, englishCues, maoriCues, userPrefs])

  // Show loading indicator while subtitles are loading
  if (loading) {
    return null // Silent loading
  }

  // Show error if subtitle loading failed
  if (error) {
    console.error('Subtitle error:', error)
    return null // Silent error (could show an error message if desired)
  }

  // Hide subtitles when chapter menu is open
  if (showChapterMenu) {
    return null
  }

  // In normal mode, don't render if subtitles are disabled or no active cues
  if (!userPrefs.subtitlesEnabled || !isPlaying || (activeEnglishCues.length === 0 && activeMaoriCues.length === 0)) {
    return null
  }

  // Format subtitle text based on preference
  const showEnglish = userPrefs.subtitleLanguage === 'english' || userPrefs.subtitleLanguage === 'both'
  const showMaori = userPrefs.subtitleLanguage === 'maori' || userPrefs.subtitleLanguage === 'both'
  const isDualLanguage = userPrefs.subtitleLanguage === 'both'

  // If no cues to display based on language preferences, don't render anything
  if ((!showEnglish || activeEnglishCues.length === 0) && (!showMaori || activeMaoriCues.length === 0)) {
    return null
  }

  return (
    <div className={styles.subtitlesContainer}>
      <div className={`${styles.subtitleText} ${isDualLanguage ? styles.dual : ''}`}>
        {showEnglish && activeEnglishCues.length > 0 && (
          <div className={styles.subtitleEnglish}>
            {activeEnglishCues.map((cue) => (
              <div key={cue.id} className={styles.sentenceItem}>
                {cue.text}
              </div>
            ))}
          </div>
        )}

        {showMaori && activeMaoriCues.length > 0 && (
          <div className={styles.subtitleMaori}>
            {activeMaoriCues.map((cue) => (
              <div key={cue.id} className={styles.sentenceItem}>
                {cue.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
